import { useEffect, useRef, useState } from 'react';
import type { AgentStreamPageInfo } from '@reflex/shared';
import type { AxonEvent } from '@/lib/types';

/** The slice of the load-earlier mutation the jump machinery drives. */
interface LoadEarlierLike {
  mutate: (variables: undefined, options?: { onError?: () => void }) => void;
  isPending: boolean;
}

/**
 * A rail click on an unloaded turn: keep paging until the turn's opening
 * sequence is inside the loaded window, then hand its prompt event to the
 * caller's scroll channel.
 *
 * The completion is a render-time state adjustment (the sanctioned
 * adjust-on-prop-change pattern) so no effect ever sets state
 * synchronously; the effect only fires page loads. A failed page fetch
 * abandons the jump — the settled mutation would otherwise pass every
 * guard again and re-fire forever, fastest when offline — and the user
 * can re-click the rail entry. A *successful* settle that did not lower
 * `startSeq` abandons it for the same reason: a fetch that changes no
 * state (e.g. the server left paged mode mid-jump) would pass every
 * guard again and loop.
 */
export function useJumpToTurn({
  streamPage,
  events,
  loadEarlier,
  onTargetLoaded,
}: {
  streamPage: AgentStreamPageInfo | null | undefined;
  events: AxonEvent[] | undefined;
  loadEarlier: LoadEarlierLike;
  onTargetLoaded: (eventId: string) => void;
}): { jumpSeq: number | null; jumpToTurn: (seqNumber: number) => void } {
  const [jumpSeq, setJumpSeq] = useState<number | null>(null);

  if (jumpSeq !== null && streamPage && streamPage.startSeq <= jumpSeq) {
    const target = (events ?? []).find((e) => e.sequence === jumpSeq);
    setJumpSeq(null);
    if (target) onTargetLoaded(target.id);
  } else if (jumpSeq !== null && streamPage && !streamPage.hasMore && !loadEarlier.isPending) {
    // No further pages can ever cover the target; an unclearable pending
    // jump would pin the rail's spinner forever. Render-time adjustment,
    // like the completion above, so the effect never sets state.
    setJumpSeq(null);
  }

  const { mutate, isPending } = loadEarlier;
  // The window start each fetch was fired from, and the previous run's
  // pending flag — together they detect a mutation that settled (pending
  // fell back to false) without moving the window.
  const firedFromSeq = useRef<number | null>(null);
  const wasPending = useRef(false);
  useEffect(() => {
    const settledNow = wasPending.current && !isPending;
    wasPending.current = isPending;
    if (jumpSeq === null || !streamPage) return;
    // Loaded jumps complete in render above; this only pages toward one.
    if (streamPage.startSeq <= jumpSeq) return;
    if (isPending) return;
    if (
      settledNow &&
      firedFromSeq.current !== null &&
      streamPage.startSeq >= firedFromSeq.current
    ) {
      // The fetch settled without lowering the window start: nothing this
      // effect reacts to changed, so firing again would loop. Stand down.
      setJumpSeq(null);
      return;
    }
    // A window with no further pages is abandoned at render time above;
    // firing from it would fetch nothing and loop.
    if (!streamPage.hasMore) return;
    firedFromSeq.current = streamPage.startSeq;
    mutate(undefined, { onError: () => setJumpSeq(null) });
  }, [jumpSeq, streamPage, isPending, mutate]);

  return { jumpSeq, jumpToTurn: setJumpSeq };
}
